/*
 * The smallest status mark there is, for a table cell with no room for a badge.
 *
 * Two rules hold it together.
 *
 * The label is required and is always rendered, visually hidden by default. A
 * dot with no label is a pixel that means something to whoever wrote the legend
 * and nothing to anybody else, and it is completely silent to a screen reader
 * reading down a column.
 *
 * Each tone has its own shape, not only its own colour. Disc, ringed disc,
 * diamond, hollow ring and dash are five marks that stay distinguishable
 * printed in grey, at eight pixels, and to a reader who cannot separate Forest
 * from Kola. The token file's status colours are chosen for contrast against
 * the page rather than against each other, so relying on hue alone here would
 * be relying on a property nothing guarantees.
 *
 * The `pill` variant puts the same mark and the same word inside a tinted,
 * rounded ground, for the places a status is read rather than scanned: a card,
 * a heading, a line of prose. It adds a channel and removes none, which is the
 * only reason it is allowed to exist alongside the bare mark.
 */

import type { HTMLAttributes } from 'react'
import type { Tone } from '../tone'
import styles from './StatusDot.module.css'

export interface StatusDotProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone?: Tone
  /** Required. What this mark means, in words. */
  label: string
  /** Prints the label beside the mark as well as exposing it. */
  labelVisible?: boolean
  /**
   * `pill` wraps the mark and its label in a tinted, rounded pill, for a status
   * that sits in prose or on a card rather than in a dense column.
   *
   * It prints the label whatever `labelVisible` says, because a pill with a
   * hidden label is a coloured blob with padding, which is the failure this
   * component exists to prevent rather than a configuration of it.
   *
   * The difference from `Badge` is worth stating, since the two now look
   * related. `Badge` sets its word in tracked uppercase and is built for a
   * status column scanned down. `StatusDot` carries a mark whose *shape*
   * differs per tone, so it stays legible printed in grey and to a reader who
   * cannot separate Forest from Kola, and it sets its word as ordinary text
   * beside that mark. Where a status is read rather than scanned, this is the
   * one to use.
   */
  variant?: 'inline' | 'pill'
}

export function StatusDot({
  tone = 'neutral',
  label,
  labelVisible = false,
  variant = 'inline',
  className,
  ...rest
}: StatusDotProps) {
  const pill = variant === 'pill'
  const rootClassName = [styles.root, pill ? styles.pill : '', className].filter(Boolean).join(' ')

  return (
    /*
     * The tone reaches the pill's tint as an attribute, and the mark's shape as
     * a class. They are not the same kind of thing: the class picks a geometry
     * that has to survive being printed, and the attribute picks three colours.
     * `Metric` already carries its delta's tone the same way.
     */
    <span {...rest} className={rootClassName} data-tone={tone}>
      <span className={[styles.mark, styles[tone]].filter(Boolean).join(' ')} aria-hidden="true" />
      <span className={labelVisible || pill ? styles.label : 'visually-hidden'}>{label}</span>
    </span>
  )
}
